import {
    AppIdentifiers,
    AppInitUserMode,
    AreaType,
    ElementType,
    IAppClosedTopicPayload,
    IClientStateChangedData,
    ILogger,
    ILoginFailedData,
    ILoginSuccessData,
    IMessageActionTakenData,
    IMessageDisplayedData,
    IUserInteractionChannel,
    OpeningMode,
    ProductPackageToID,
    SourceType,
} from '@sparkware/uc-sdk-core';
import { ClientIntegrationFacadeToken, WindowToken } from '../../injection-tokens';
import Container from 'typedi';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
} from '@unified-client/event-formatter';
import {
    IABTestResult,
    IActionHandlerStepResult,
    IPaymentsWidgetEligibilityResponse,
    IMCRResult,
} from '../../models/bi-events/IActionHandlerStepResult';
import {
    IIdentityChannel,
    ITopicEnvelope,
    IUiChannel,
    MessageBroker,
    IAppLaunchInfo,
} from '@sparkware/uc-sdk-core';
import { LoggerProvider } from '../logger';

import { ClickStreamTrackingProvider } from '../tracking';
import { EventType } from '@unified-client/event-formatter/dist/models';
import { IAppAuthenticationResult } from '../../models/bi-events/IAppAuthenticationResult';
import { IAppClosed } from '../../models/bi-events/IAppClosed';
import { IAppInitRequest } from '../../models/bi-events/IAppInitRequest';
import { IAppInitResult } from '../../models/bi-events/IAppInitResult';
import { IAuthenticationResult } from '../../models/bi-events/IAuthenticationResult';
import { IBIEvent } from '../../models/bi-events/IBIEvent';
import { ICorrelation } from '../action-handler/models/ICorrelation';
import { IDeviceInfo } from '../../models/bi-events/IDeviceInfo';
import { IMessageActionTaken } from '../../models/bi-events/IMessageActionTaken';
import { INavigationRequest } from '../../models/bi-events/INavigationRequest';
import { INavigationResult } from '../../models/bi-events/INavigationResult';
import { IOpenAppRequest } from '../../models/bi-events/IOpenAppRequest';
import { ISessionClosedResult } from '../../models/bi-events/ISessionClosedResult';
import { MessageActionType } from '../ui/enums/MessageActionType';
import { ClientProvider } from '../../models/enums/general-enums';
import { Utils } from '../utils';
import StringUtils from '../../../Modules/Utils/StringUtils';
import { LocalSimpleStoreService } from '../storage/implementations/simple-store';
import { IClientStateChanged } from '../../models/bi-events/IClientStateChanged';
import { AppState } from '../app-state';
import { IBIEventHandler } from './models/IBIEventHandler';
import { IAppMaximizedData } from '../app-launcher/models/IAppMaximizedData';
import { IAppMinimizedData } from '../app-launcher/models/IAppMinimizedData';
import LogIndex from '../tracking/enums/LogIndex';

export class BIEventHandler implements IBIEventHandler {
    private wrapperLoginCorrelationId: string;
    private readonly _logger: ILogger;
    private readonly _window: Window;
    private readonly _appState: AppState;
    private readonly _clickStreamTrackingProvider: ClickStreamTrackingProvider;
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _identityChannel: IIdentityChannel;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _uiChannel: IUiChannel;
    private readonly _userInteractionChannel: IUserInteractionChannel;
    private readonly _utils: Utils;

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('BIEventHandler');
        this._identityChannel = MessageBroker.getInstance().identity;
        this._uiChannel = MessageBroker.getInstance().ui;
        const sessionManagerChannel = MessageBroker.getInstance().session;
        this._userInteractionChannel = MessageBroker.getInstance().userinteraction;
        this._utils = Container.get(Utils);
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._appState = Container.get(AppState);
        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('BIEventHandler');
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this.wrapperLoginCorrelationId = null;

        const { loginFailSubscribe, loginSuccessSubscribe } = Container.get(
            ClientIntegrationFacadeToken,
        );

        this._identityChannel.topics.loginFailed.subscribe(this._onLoginFail.bind(this));
        this._identityChannel.topics.loginSuccess.subscribe(this._onLoginSuccess.bind(this));

        this._uiChannel.topics.messageDisplayed.subscribe(this._onMessageDisplayed.bind(this));

        this._uiChannel.topics.messageActionTaken.subscribe(this._onMessageActionTaken.bind(this));
        this._userInteractionChannel.topics.Regulation_Click.subscribe(this._trackRegulationClick);

        loginFailSubscribe(this._onUserAuthenticationFailed.bind(this));
        loginSuccessSubscribe(this._onUserAuthenticationSuccess.bind(this));
        sessionManagerChannel.topics.appInitSucceeded.subscribe(
            this._onAppInitSucceeded.bind(this),
        );
        sessionManagerChannel.topics.appInitFailed.subscribe(this._onAppInitFailed.bind(this));
        this._userInteractionChannel.topics.clientStateChanged.subscribe(
            this._onClientStateChanged.bind(this),
        );
    }

    public sendActionHandlerEvent<
        T extends IPaymentsWidgetEligibilityResponse | IABTestResult | IMCRResult,
    >(eventData: IActionHandlerStepResult<T>, correlation: ICorrelation) {
        this._sendEventSafely(() => {
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;

            this.wrapperLoginCorrelationId = this._utils.generateCorrelationID();
            if (this.hasMessageID(eventData, 'uc_action_handler_result')) {
                const formatter =
                    this._eventFormatterBuilder.createFormatter('sendActionHandlerEvent');

                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_action_handler_result',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        correlationID: correlation?.correlationID || this.wrapperLoginCorrelationId,
                        businessCorrelationID: correlation?.businessCorrelationID,
                        productPackageID: productPackage.toString(),
                    },
                    eventData,
                );

                this.wrapperLoginCorrelationId = null;

                this._clickStreamTrackingProvider.sendEventV2(event);
            }
        });
    }

    public sendAppInitRequest(
        targetAppID: AppIdentifiers,
        launchInfo: IAppLaunchInfo<unknown>,
        openingMode: OpeningMode,
        userMode: AppInitUserMode,
        productPackageToID: ProductPackageToID,
        correlationID: string,
    ) {
        this._sendEventSafely(() => {
            const timeZoneOffset = new Date().getTimezoneOffset();
            const { subBrandId } = this._window.pageContextManager.getBrandData();
            const { currency, clientLangIso3 } =
                this._window.pageContextManager.getLocalizationData();
            const { productPackage } = this._window.pageContextManager.getDeviceData();
            const userData = this._window.pageContextManager.getUserData();
            const regulationData = this._window.pageContextManager.getRegulationData();
            const eventData: IAppInitRequest = {
                targetAppID,
                messageID: this.getMessageID(),
                subBrandID: subBrandId,
                currencyCode: currency,
                language: clientLangIso3,
                sequentialCorrelationID:
                    this._utils.getHighestNumber(
                        launchInfo.sequentialCorrelationID,
                        this._appState
                            .getSeqCorrIdHandler(launchInfo.businessCorrelationID)
                            .getCurrentValue(),
                    ) || 1,
                productPackageFromID: productPackage,
                productPackageToID: productPackageToID,
                launchInfo: {
                    channel: {
                        area: launchInfo.channel.area,
                        element: launchInfo.channel.element,
                        source: launchInfo?.channel.source || SourceType.UD,
                    },
                    sourceAppID: launchInfo.sourceAppID,
                    sourceAppVersion: launchInfo.sourceAppVersion,
                    trigger: launchInfo.trigger,
                    openingMode,
                    appSpecificParameters: launchInfo.appSpecificParameters,
                },
                userInfo: {
                    userMode,
                    regulationTypeID: regulationData.regulationTypeId,
                    isVip: userData.isUserVip,
                    isFTD: userData.isFtd,
                    isPromotionPermitted: userData.isPromotionPermitted,
                    RMS: userData.rms,
                },
                timeZoneUTCOffset: timeZoneOffset < 0 ? timeZoneOffset / -60 : timeZoneOffset / 60,
                deviceInfo: this._getDeviceInfo(),
            };

            if (this.hasMessageID(eventData, 'uc_app_init_request')) {
                correlationID = correlationID || this._utils.generateCorrelationID();
                const formatter =
                    this._eventFormatterBuilder.createFormatter('sendAppInitRequestEvent');

                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_app_init_request',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        businessCorrelationID: launchInfo?.businessCorrelationID,
                        correlationID,
                        productPackageID: productPackage.toString(),
                    },
                    eventData,
                );
                this._clickStreamTrackingProvider.sendEventV2(event);
            }
        });
    }

    public sendOpenAppEvent(
        targetAppID: AppIdentifiers,
        launchInfo: IAppLaunchInfo<unknown>,
        correlationID: string,
    ) {
        this._sendEventSafely(() => {
            const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;
            const eventData: IOpenAppRequest = {
                targetAppID,
                messageID: this.getMessageID(),
                subBrandID: subBrandId,
                launchInfo: {
                    channel: {
                        area: launchInfo.channel.area,
                        element: launchInfo.channel.element,
                        source: launchInfo?.channel.source || SourceType.UD,
                    },
                    sourceAppID: launchInfo.sourceAppID,
                    sourceAppVersion: launchInfo.sourceAppVersion,
                    trigger: launchInfo.trigger,
                },
                sequentialCorrelationID:
                    this._utils.getHighestNumber(
                        launchInfo.sequentialCorrelationID,
                        this._appState
                            .getSeqCorrIdHandler(launchInfo.businessCorrelationID)
                            .getCurrentValue(),
                    ) || 1,
            };
            correlationID = correlationID || this._utils.generateCorrelationID();

            if (this.hasMessageID(eventData, 'uc_open_app_request')) {
                const formatter = this._eventFormatterBuilder.createFormatter('sendOpenAppEvent');

                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_open_app_request',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        correlationID,
                        businessCorrelationID: launchInfo?.businessCorrelationID,
                        productPackageID: productPackage.toString(),
                    },
                    eventData,
                );
                this._clickStreamTrackingProvider.sendEventV2(event);
            }
        });
    }

    public sendAppClosedEvent(data: IAppClosedTopicPayload) {
        this._sendEventSafely(() => {
            this._logger.debug(`[sendAppClosedEvent] start`);
            const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;
            const { launchInfo, appID: targetAppID } = data ?? {};

            const businessCorrelationID =
                launchInfo?.businessCorrelationID ||
                this._appState.getBusinessCorrelationId(targetAppID);

            const sequentialCorrelationID = Number(data?.launchInfo?.sequentialCorrelationID ?? 1);
            const eventData: IAppClosed = {
                targetAppID,
                messageID: this.getMessageID(),
                subBrandID: subBrandId,
                sequentialCorrelationID: this._utils.getHighestNumber(
                    !isNaN(sequentialCorrelationID) ? sequentialCorrelationID : 1,
                    this._appState.getSeqCorrIdHandler(businessCorrelationID)?.getCurrentValue() ||
                        -1,
                ),
                appCloseReason: data.closeReason,
                channel: {
                    area: data.channel?.area || AreaType.UD,
                    element: data.channel?.element || ElementType.UD,
                    source: data.channel?.source || SourceType.UD,
                },
                sourceAppID: launchInfo?.sourceAppID,
                sourceAppVersion: launchInfo?.sourceAppVersion,
            };

            if (this.hasMessageID(eventData, 'uc_app_closed')) {
                const correlationID = data.correlationID ?? this._utils.generateCorrelationID();
                const formatter = this._eventFormatterBuilder.createFormatter('sendAppClosedEvent');
                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_app_closed',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        businessCorrelationID,
                        correlationID,
                        productPackageID: productPackage.toString(),
                    },
                    eventData,
                );
                this._clickStreamTrackingProvider.sendEventV2(event);
            }
        });
    }

    public sendNavigationRequestEvent(url: string) {
        this._sendEventSafely(() => {
            const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;
            const eventData: INavigationRequest = {
                URL: url,
                messageID: this.getMessageID({ hasSequentialNumber: false }),
                subBrandID: subBrandId,
            };

            const formatter = this._eventFormatterBuilder.createFormatter(
                'sendNavigationRequestEvent',
            );
            const event = formatter.formatBusinessEvent(
                {
                    eventName: 'uc_navigation_request',
                    eventType: [EventType.Log],
                },
                {
                    correlationID: this._utils.generateCorrelationID(),
                    productPackageID: productPackage.toString(),
                },
                eventData,
            );

            this._clickStreamTrackingProvider.sendEventV2(event);
        });
    }

    public sendNavigationResultEvent(
        url: string,
        isOk: boolean,
        errorCode?: number,
        errorDescription?: string,
    ) {
        this._sendEventSafely(() => {
            const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;
            const eventData: INavigationResult = {
                URL: url,
                isOk,
                errorCode,
                errorDescription: StringUtils.toString(errorDescription),
                messageID: this.getMessageID({ hasSequentialNumber: false }),
                subBrandID: subBrandId,
            };

            const formatter = this._eventFormatterBuilder.createFormatter(
                'sendNavigationResultEvent',
            );
            const event = formatter.formatBusinessEvent(
                {
                    eventName: 'uc_navigation_result',
                    eventType: [EventType.Log],
                },
                {
                    correlationID: this._utils.generateCorrelationID(),
                    productPackageID: productPackage.toString(),
                },
                eventData,
            );

            this._clickStreamTrackingProvider.sendEventV2(event);
        });
    }

    public sendSdkDownloadEvent(correlationID: string, errorDescription) {
        this._sendEventSafely(() => {
            const formatter = this._eventFormatterBuilder.createFormatter('sendSdkDownloadEvent');
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;
            const eventData = {
                errorDescription: StringUtils.toString(errorDescription),
            };
            const event = formatter.formatBusinessEvent(
                {
                    eventName: 'uc_app_sdk_download',
                    eventType: [EventType.Log],
                },
                {
                    correlationID,
                    productPackageID: productPackage.toString(),
                },
                eventData,
            );
            this._clickStreamTrackingProvider.sendEventV2(event);
        });
    }

    public sendMessageActionTakenEvent(
        correlationID: string,
        businessCorrelationID: string,
        actionType: MessageActionType,
        launchInfo: IAppLaunchInfo<unknown>,
        eventPromise: Promise<void>,
    ) {
        this._sendEventSafely(() => {
            const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;
            const eventData: IMessageActionTaken = {
                messageID: this.getMessageID(),
                subBrandID: subBrandId,
                sequentialCorrelationID: launchInfo?.sequentialCorrelationID ?? 1,
                actionType: actionType,
            };

            if (this.hasMessageID(eventData, 'uc_app_message_action_taken')) {
                const formatter = this._eventFormatterBuilder.createFormatter(
                    'sendMessageActionTakenEvent',
                );
                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_app_message_action_taken',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        correlationID,
                        businessCorrelationID: businessCorrelationID,
                        productPackageID: productPackage.toString(),
                    },
                    eventData,
                );
                this._clickStreamTrackingProvider.sendEventV2(event, eventPromise);
            }
        });
    }

    public sendAuthenticationResultFromAutologin = () => {
        this._sendEventSafely(() => {
            this._sendAuthenticationResultEvent(this._getSuccessfullAuthenticationResult());
        });
    };

    public sendSessionClosedEvent(eventData: ISessionClosedResult) {
        this._sendEventSafely(() => {
            if (this.hasMessageID(eventData, 'uc_session_closed')) {
                const productPackage =
                    this._window.pageContextManager.getDeviceData().productPackage;
                const globalSessionId =
                    this._window.pageContextManager.getSessionData().globalSessionId;

                const existingGlobalSessionId = this._localSimpleStoreService.get(
                    'globalSessionId_sessionClosed',
                );

                if (existingGlobalSessionId) {
                    return;
                }

                this._localSimpleStoreService.set('globalSessionId_sessionClosed', globalSessionId);

                this.wrapperLoginCorrelationId = this._utils.generateCorrelationID();
                const formatter =
                    this._eventFormatterBuilder.createFormatter('sendSessionClosedEvent');

                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_session_closed',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        correlationID: this.wrapperLoginCorrelationId,
                        productPackageID: productPackage.toString(),
                    },
                    eventData,
                );

                this.wrapperLoginCorrelationId = null;

                this._clickStreamTrackingProvider.sendEventV2(event);
            }
        });
    }

    public sendAppInitResultTimeout({
        appID,
        correlationID,
        launchInfo,
    }: {
        appID: AppIdentifiers;
        correlationID: string;
        launchInfo: IAppLaunchInfo<unknown>;
    }) {
        this._sendEventSafely(() => {
            const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;

            const eventData: IAppInitResult = {
                errorCode: 1,
                errorDescription: 'Timeout',
                sequentialCorrelationID: launchInfo?.sequentialCorrelationID,
                isOk: false,
                targetAppID: appID,
                messageID: this.getMessageID(),
                subBrandID: subBrandId,
            };

            this._sendAppInitResultEvent(eventData, launchInfo, correlationID);
        });
    }

    public getMessageID = ({
        appID = AppIdentifiers.UnifiedClient,
        hasSequentialNumber = true,
        resetSequentialNumber = false,
    }: {
        appID?: string;
        hasSequentialNumber?: boolean;
        resetSequentialNumber?: boolean;
    } = {}): string => {
        const isNativeSDK = this._utils.isNativeSDK();
        if (appID === AppIdentifiers.UnifiedClient && isNativeSDK) {
            appID = AppIdentifiers.UnifiedClientNative;
        }

        const CID: number = this._window.pageContextManager.getUserData()?.cid;
        const playerSessionID: number =
            this._window.pageContextManager.getSessionData().playerSessionId;

        if (resetSequentialNumber) {
            this._clickStreamTrackingProvider.resetSequentialNumber();
        }

        const messageFields = [
            CID,
            playerSessionID,
            appID,
            hasSequentialNumber
                ? this._clickStreamTrackingProvider.SequentialNumber + 1
                : undefined,
        ];

        return messageFields.filter((elem) => elem).join('_');
    };

    sendAppMinimize(appMinimizedData: IAppMinimizedData): void {
        const {
            appID,
            correlationID = this._utils.generateCorrelationID(),
            launchInfo = {} as IAppLaunchInfo,
            appData = {},
        } = appMinimizedData;
        const {
            sequentialCorrelationID = 1,
            businessCorrelationID = '',
            channel = {},
        } = launchInfo;
        this._sendEventSafely(() => {
            const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
            const eventData = {
                targetAppID: appID,
                messageID: this.getMessageID(),
                subBrandID: subBrandId,
                sequentialCorrelationID: sequentialCorrelationID,
                channel: this._utils.isEmptyObject(channel)
                    ? {
                          source: 'Unified Client',
                          area: 'Minimize button',
                          element: 'Minimize button',
                      }
                    : channel,
                ...appData,
            };

            if (this.hasMessageID(eventData, 'uc_minimize_request')) {
                const productPackage =
                    this._window.pageContextManager.getDeviceData().productPackage;
                const formatter =
                    this._eventFormatterBuilder.createFormatter('sendAppMinimizeEvent');
                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_minimize_request',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        businessCorrelationID,
                        correlationID,
                        productPackageID: productPackage.toString(),
                    },
                    eventData,
                );
                this._clickStreamTrackingProvider.sendEventV2(event);
            }
        });
    }

    public sendAppMaximize(appMaximizedData: IAppMaximizedData): void {
        const {
            appID,
            correlationID = this._utils.generateCorrelationID(),
            launchInfo = {} as IAppLaunchInfo,
            appData = {},
        } = appMaximizedData;
        const { sequentialCorrelationID = 1, businessCorrelationID = '', channel } = launchInfo;
        this._sendEventSafely(() => {
            const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
            const eventData = {
                targetAppID: appID,
                messageID: this.getMessageID(),
                subBrandID: subBrandId,
                sequentialCorrelationID,
                channel,
                ...appData,
            };

            if (this.hasMessageID(eventData, 'uc_maximize_request')) {
                const productPackage =
                    this._window.pageContextManager.getDeviceData().productPackage;
                const formatter =
                    this._eventFormatterBuilder.createFormatter('sendAppMaximizeEvent');
                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_maximize_request',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        businessCorrelationID,
                        correlationID,
                        productPackageID: productPackage.toString(),
                    },
                    eventData,
                );
                this._clickStreamTrackingProvider.sendEventV2(event);
            }
        });
    }

    private _sendEventSafely = (eventSender: any) => {
        this._logger.debug(`[sendEventSafely] start`);
        try {
            eventSender();
            this._logger.debug(`[sendEventSafely] finished`);
        } catch (err) {
            this._logger.error(`[sendEventSafely] failed`, err);
        }
    };

    private _sendMessageDisplayedEvent(data: IMessageDisplayedData) {
        const {
            targetAppID,
            displayMessageID,
            displayMessageName,
            correlationID,
            launchInfo,
            extraData = {},
        } = data;

        const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
        const productPackage = this._window.pageContextManager.getDeviceData().productPackage;
        const eventData = {
            targetAppID,
            messageID: this.getMessageID(),
            subBrandID: subBrandId,
            sequentialCorrelationID: launchInfo?.sequentialCorrelationID ?? 1,
            displayMessageID,
            displayMessageName,
        };

        if (this.hasMessageID(eventData, 'uc_app_message_displayed')) {
            const formatter = this._eventFormatterBuilder.createFormatter(
                'sendMessageDisplayedEvent',
            );
            const eventName = 'uc_app_message_displayed';
            const event = formatter.formatBusinessEvent(
                {
                    eventName,
                    eventType: [EventType.Log, EventType.Analytics],
                },
                {
                    correlationID,
                    businessCorrelationID: launchInfo?.businessCorrelationID,
                    productPackageID: productPackage.toString(),
                },
                eventData,
            );

            /**
             * TODO - this fix need to be done inside the FORMAT EVENTS PROJECT
             */
            const temporaryEventFix = { ...event };
            if (!this._utils.isEmptyObject(extraData)) {
                temporaryEventFix.eventData['extraData'] = extraData;
            }
            this._clickStreamTrackingProvider.sendEventV2(temporaryEventFix);
        }
    }

    private _onUserAuthenticationFailed({ error }) {
        const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
        const eventData: IAppAuthenticationResult = {
            ...error,
            isOk: false,
            messageID: this.getMessageID(),
            subBrandID: subBrandId,
            playerSessionId: undefined,
            globalSessionId: undefined,
        };

        this._sendAppAuthenticationResultEvent(eventData);
    }

    private _onUserAuthenticationSuccess() {
        const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
        const clientProvider = this._window.pageContextManager.getClientProviderData().name;
        const sessionData = this._window.pageContextManager.getSessionData();
        const eventData: IAppAuthenticationResult = {
            targetAppID:
                clientProvider === ClientProvider.Kambi
                    ? AppIdentifiers.SportKambi
                    : clientProvider === ClientProvider.Spectate
                      ? AppIdentifiers.SportSpectate
                      : AppIdentifiers.SportPoker,
            errorCode: undefined,
            errorDescription: undefined,
            isOk: true,
            messageID: this.getMessageID(),
            subBrandID: subBrandId,
            ...sessionData,
        };

        this._sendAppAuthenticationResultEvent(eventData);
    }

    private _getSuccessfullAuthenticationResult = (): IAuthenticationResult => {
        const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;

        return {
            errorCode: undefined,
            errorDescription: undefined,
            deviceInfo: this._getDeviceInfo(),
            isOk: true,
            messageID: this.getMessageID({ resetSequentialNumber: true }),
            subBrandID: subBrandId,
        };
    };

    private _onLoginSuccess = (data: ILoginSuccessData, envelope: ITopicEnvelope) => {
        this._localSimpleStoreService.remove('globalSessionId_sessionClosed');

        this.wrapperLoginCorrelationId = this._utils.generateCorrelationID();
        if (envelope.headers.publisher === 'UCF.SDK.SessionManager') return;

        this._sendAuthenticationResultEvent(this._getSuccessfullAuthenticationResult());
    };

    private _onLoginFail = (data: ILoginFailedData) => {
        const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;

        const eventData: IAuthenticationResult = {
            errorCode: data.errorCode,
            errorDescription: StringUtils.toString(data.errorDescription),
            deviceInfo: this._getDeviceInfo(),
            isOk: false,
            messageID: this.getMessageID({ resetSequentialNumber: true }),
            subBrandID: subBrandId,
        };

        this._sendAuthenticationResultEvent(eventData);
    };

    private _onMessageDisplayed = (data: IMessageDisplayedData) => {
        this._sendMessageDisplayedEvent(data);
    };

    private _onMessageActionTaken = (data: IMessageActionTakenData) => {
        this.sendMessageActionTakenEvent(
            data.correlationID,
            data.businessCorrelationID,
            data.actionType,
            data.launchInfo,
            data.eventPromise,
        );
    };

    private _sendAppAuthenticationResultEvent(eventData: IAppAuthenticationResult) {
        if (this.hasMessageID(eventData, 'uc_app_authentication_result')) {
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;

            const formatter = this._eventFormatterBuilder.createFormatter(
                'sendAppAuthenticationResultEvent',
            );

            const event = formatter.formatBusinessEvent(
                {
                    eventName: 'uc_app_authentication_result',
                    eventType: [EventType.Log, EventType.Analytics],
                },
                {
                    correlationID:
                        this.wrapperLoginCorrelationId || this._utils.generateCorrelationID(),
                    productPackageID: productPackage.toString(),
                },
                eventData,
            );

            this._clickStreamTrackingProvider.sendEventV2(event);
        }
    }

    private _sendAuthenticationResultEvent(eventData: IAuthenticationResult) {
        const productPackage = this._window.pageContextManager.getDeviceData().productPackage;
        this.wrapperLoginCorrelationId = this._utils.generateCorrelationID();
        const formatter = this._eventFormatterBuilder.createFormatter(
            'sendAuthenticationResultEvent',
        );

        if (this.hasMessageID(eventData, 'uc_authentication_result')) {
            const event = formatter.formatBusinessEvent(
                {
                    eventName: 'uc_authentication_result',
                    eventType: [EventType.Log, EventType.Analytics],
                },
                {
                    correlationID: this.wrapperLoginCorrelationId,
                    productPackageID: productPackage.toString(),
                },
                eventData,
            );

            this._clickStreamTrackingProvider.sendEventV2(event);
        }
    }

    private _onAppInitSucceeded({
        appID: targetAppID,
        correlationID,
        launchInfo,
    }: {
        appID: AppIdentifiers;
        correlationID: string;
        launchInfo: IAppLaunchInfo<unknown>;
    }) {
        const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
        const eventData: IAppInitResult = {
            errorCode: undefined,
            errorDescription: undefined,
            sequentialCorrelationID: launchInfo?.sequentialCorrelationID,
            isOk: true,
            targetAppID,
            messageID: this.getMessageID(),
            subBrandID: subBrandId,
        };

        this._sendAppInitResultEvent(eventData, launchInfo, correlationID);
    }

    private _onAppInitFailed({
        appID: targetAppID,
        errorCode,
        errorDescription,
        launchInfo,
        correlationID,
    }: {
        appID: AppIdentifiers;
        errorCode: number;
        errorDescription: string;
        launchInfo?: IAppLaunchInfo<unknown>;
        correlationID?: string;
    }) {
        //TODO add launchinfo here + correlationID
        const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;

        const eventData: IAppInitResult = {
            targetAppID,
            errorCode,
            errorDescription: StringUtils.toString(errorDescription),
            sequentialCorrelationID: launchInfo?.sequentialCorrelationID,
            isOk: false,
            messageID: this.getMessageID(),
            subBrandID: subBrandId,
        };

        this._sendAppInitResultEvent(eventData, launchInfo, correlationID);
    }

    private _sendAppInitResultEvent(
        eventData: IAppInitResult,
        launchInfo?: IAppLaunchInfo<unknown>,
        correlationID?: string,
    ) {
        try {
            if (this.hasMessageID(eventData, 'uc_app_init_result')) {
                const businessCorrelationID =
                    launchInfo?.businessCorrelationID ??
                    this._appState.getBusinessCorrelationId(eventData.targetAppID);
                const sequentialCorrelationID =
                    this._utils.getHighestNumber(
                        launchInfo?.sequentialCorrelationID,
                        this._appState.getSeqCorrIdHandler(businessCorrelationID).getCurrentValue(),
                    ) || 1;

                const productPackage = StringUtils.toString(
                    this._window.pageContextManager.getDeviceData().productPackage,
                );
                const formatter =
                    this._eventFormatterBuilder.createFormatter('sendAppInitResultEvent');
                const event = formatter.formatBusinessEvent(
                    {
                        eventName: 'uc_app_init_result',
                        eventType: [EventType.Log, EventType.Analytics],
                    },
                    {
                        businessCorrelationID,
                        correlationID: correlationID ?? this._utils.generateCorrelationID(),
                        productPackageID: productPackage,
                    },
                    { ...eventData, sequentialCorrelationID },
                );
                this._clickStreamTrackingProvider.sendEventV2(event);
            }
        } catch (err) {
            this._logger.error(`[_sendAppInitResultEvent] failed`, err);
        }
    }

    private hasMessageID = (eventData: IBIEvent, eventName: string) => {
        const hasMessageId = !!eventData?.messageID;
        if (!hasMessageId) {
            this._logger.warn(
                `${eventName}: DataStore is no longer in authenticated mode, the current message will be dismissed.`,
            );
        }

        return hasMessageId;
    };

    private _getDeviceInfo = (): IDeviceInfo => {
        const { deviceTypeId, browser, isHybrid } = this._window.pageContextManager.getDeviceData();
        const proxyType = this._window.pageContextManager.getNetworkData().proxyType;
        const windowObject = Container.get(WindowToken);

        return {
            deviceTypeID: deviceTypeId,
            isHybrid: isHybrid.toString() === 'true',
            isNative: this._utils.findIfIsNative(),
            os: this._window.pageContextManager.getDeviceData().osName,
            browserType: browser,
            proxyType: proxyType,
            resolution:
                windowObject.screen.width * windowObject.devicePixelRatio +
                'x' +
                windowObject.screen.height * windowObject.devicePixelRatio,
        };
    };

    private _onClientStateChanged = (data: IClientStateChangedData) => {
        this._sendClientStateChangedEvent(
            data.correlationID,
            data.clientStateID,
            data.clientStateName,
        );
    };

    private _sendClientStateChangedEvent = (correlationID, clientStateID, clientStateName) => {
        const subBrandId = this._window.pageContextManager.getBrandData().subBrandId;
        const productPackage = this._window.pageContextManager.getDeviceData().productPackage;

        const eventData: IClientStateChanged = {
            subBrandID: subBrandId,
            clientStateID: clientStateID,
            clientStateName: clientStateName,
            messageID: '',
        };

        const formatter = this._eventFormatterBuilder.createFormatter(
            '_sendClientStateChangedEvent',
        );
        const event = formatter.formatBusinessEvent(
            {
                eventName: 'clientStateChanged',
                eventType: [EventType.Log, EventType.Analytics],
            },
            {
                correlationID,
                productPackageID: productPackage.toString(),
            },
            eventData,
        );
        this._clickStreamTrackingProvider.sendEventV2(event);
    };

    private _trackRegulationClick = (
        { eventData, target } = { eventData: undefined, target: undefined },
    ) => {
        if (eventData) {
            const formatter = this._eventFormatterBuilder.createFormatter('_trackRegulationClick');
            const productPackage = this._window.pageContextManager.getDeviceData().productPackage;

            const event = formatter.formatBusinessEvent(
                {
                    eventName: 'sweden_self_test',
                    eventType: [EventType.Log, EventType.Analytics],
                },
                {
                    businessCorrelationID: this._utils.generateCorrelationID(),
                    correlationID: this._utils.generateCorrelationID(),
                    productPackageID: productPackage.toString(),
                    targetOverride: target || LogIndex.UnifiedClient,
                },
                eventData,
            );

            this._clickStreamTrackingProvider.sendEventV2(event);
        }
    };
}
